/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    Pagination,
    Table,
    Tooltip,
} from "@jhool-io/fe-components";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
    IBillingResponse,
    IMatchedPaymentResponse,
    ISplitPaymentPayload,
    ISplitPaymentPayloadSingleBill,
    IUnmatchedPaymentResponse,
    PaymentStatus,
} from "../../../../utils/types/billing";
import {
    buildSuccessMessageForMatchedPayments,
    cn,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    handleRemoveEmptyParamFromQueryParams,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import { useFetchClientUnmatchedPayment } from "../../../../hooks/queries/billing";
import {
    CPT_CODES_DESCRIPTION,
    LISTS_DEFAULT_LIMIT,
} from "../../../../utils/constants";
import ListState from "../../../../components/ListState/ListState";
import {
    useAddUnmatchedPayment,
    useSplitPayment,
} from "../../../../hooks/mutations/billing";
import useToast from "../../../../hooks/useToast";
import { IPaginatedApiResponse } from "../../../../utils/types/api-response";
import { getBillStatusTag } from "../../helpers/billing.helpers";
import { BillPaymentType, BillStatus } from "../../types/billing.types";
import { useFetchInvoices } from "../../hooks/queries/billing.queries";
import InfoIcon from "../../../../icons/Info";
import Loader from "../../../../shared-ui/Loader/Loader";
import { NoteLabels, NoteTypes } from "../../../notes/types/notes.types";

interface UnmatchedPaymentListProps {
    /** Invoice/bill balance */
    invoiceBalance: number;
    /** Invoice/bill in view id */
    invoiceId: string;
    /** Function to call after performing action in modal */
    onFormSubmit: () => void;
    /** Client session date of service */
    dateOfService: string;
}

export default function UnmatchedPaymentList({
    invoiceBalance,
    invoiceId,
    onFormSubmit,
    dateOfService,
}: UnmatchedPaymentListProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [showSplitPaymentModal, setShowSplitPaymentModal] =
        React.useState(false);

    // For single bill unmatched payments
    const [getSelectedBillPayments, setGetSelectedBillPayments] =
        React.useState<Row<IUnmatchedPaymentResponse>[]>();
    const [rowSelection, setRowSelection] = React.useState({});
    // For selected bills
    const [getSelectedBillsData, setGetSelectedBillsData] =
        React.useState<Row<IBillingResponse>[]>();
    const [rowSelectionInvoices, setRowSelectionInvoices] = React.useState({});

    const params = useParams();
    const clientId = params.clientId as string;

    const unmatchPageFilter = Number(searchParams.get("unmatch_page")) || 1;
    const unmatchPaymentStatusFilter = searchParams.get("payment_status") || "";
    const unmatchLimitFilter =
        Number(searchParams.get("unmatch_limit")) || LISTS_DEFAULT_LIMIT;
    const paymentTypeFilter = searchParams.get("payment_type") || "Credit";

    const invoicesPageFilter = Number(searchParams.get("invoice_page")) || 1;
    const invoicesLimitFilter =
        Number(searchParams.get("invoice_limit")) || LISTS_DEFAULT_LIMIT;
    const canSplitPayment = searchParams.get("split_payment");

    // React-hook-form for split payment form
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        watch,
    } = useForm<ISplitPaymentPayloadSingleBill>({
        resolver: yupResolver(
            yup.object().shape({
                bill: yup.object().shape({
                    amount: yup
                        .string()
                        .matches(
                            /^(?!^\.)(\d+(\.\d+)?|\.\d+)$/,
                            "Please enter a valid amount"
                        ),
                }),
                invoices: yup.array().of(
                    yup.object().shape({
                        amount: yup
                            .string()
                            .matches(
                                /^(?!^\.)(\d+(\.\d+)?|\.\d+)$/,
                                "Please enter a valid amount"
                            ),
                    })
                ),
            })
        ),
        mode: "onChange",
    });

    const getPaymentTypeFilterText = () => {
        if (paymentTypeFilter === "Write-off") return "Write-off";
        if (paymentTypeFilter === "Refund") return "Refund";
        if (paymentTypeFilter === "Credit") return "Credit";
        if (paymentTypeFilter === "All") return "All";
        return "Credit";
    };

    const handlePaymentTypeFilterChange = (type: string) => {
        searchParams.set("payment_type", type);
        searchParams.set("unmatch_page", "1");
        setSearchParams(searchParams);
    };

    const getPaymetTypeFilter = () => {
        if (paymentTypeFilter === "All") return [];
        if (paymentTypeFilter === "Write-off")
            return [BillPaymentType.WBI, BillPaymentType.WBCI];
        if (paymentTypeFilter === "Refund")
            return [
                BillPaymentType.RI,
                BillPaymentType.RCI,
                BillPaymentType.RIP,
                BillPaymentType.RPR100,
            ];
        return [
            BillPaymentType.I,
            BillPaymentType.CI,
            BillPaymentType.IP,
            BillPaymentType.PR100,
        ];
    };

    const getPaymentSource = (paymentType: string) => {
        if (paymentType === "pr100" || paymentType === "rpr100") {
            return "PR100";
        }
        if (
            paymentType === "i" ||
            paymentType === "wbi" ||
            paymentType === "ri"
        ) {
            return "Insurance";
        }

        if (paymentType === "ip" || paymentType === "rip") {
            return "Interest Payment";
        }

        return "Co-Insurance";
    };

    const getPaymentTypeTag = (type: string) => {
        switch (type) {
            case "wbci":
            case "wbi":
                return (
                    <Badge
                        type="neutral"
                        content={<>Write-off: {getPaymentSource(type)}</>}
                    />
                );
            case "ri":
            case "rci":
            case "rip":
            case "rpr100":
                return (
                    <Badge
                        type="success"
                        content={<>Refund: {getPaymentSource(type)}</>}
                    />
                );
            case "i":
            case "ci":
            case "ip":
            case "pr100":
                return (
                    <Badge
                        type="warning"
                        content={<>Credit: {getPaymentSource(type)}</>}
                    />
                );
            default:
                return <Badge type="neutral" content={type ?? "others"} />;
        }
    };

    const { data, isLoading, isSuccess, error } =
        useFetchClientUnmatchedPayment({
            page: unmatchPageFilter,
            payment_status: unmatchPaymentStatusFilter,
            limit: unmatchLimitFilter,
            client_id: clientId,
            show_shared_payments: true,
            payment_type: getPaymetTypeFilter(),
        });

    // Fetch invoices to split
    const invoices = useFetchInvoices({
        client_id: clientId,
        limit: invoicesLimitFilter,
        page: invoicesPageFilter,
    });

    // hook for matching payment
    const matchPayments = useAddUnmatchedPayment();

    // Mutation hook for splitting payment
    const splitPayment = useSplitPayment();

    // Query client
    const queryClient = useQueryClient();

    const { toast } = useToast();

    const formInvoices = watch("invoices");
    const bill = watch("bill");

    // Get selected unmatched payment to split
    const paymentInView =
        getSelectedBillPayments && getSelectedBillPayments[0]?.original;

    const total = getSelectedBillPayments?.reduce(
        (sum: number, item) => sum + item.original.amount,
        0
    );

    const handlePaymentStatusFilter = (paymentFilter: string) => {
        if (paymentFilter === "") searchParams.delete("payment_status");
        else searchParams.set("payment_status", paymentFilter);

        searchParams.set("unmatch_page", "1");
        setSearchParams(searchParams);
    };

    // onChange handler for unmatched payments list page filter
    const handleUnmatchedPaymentPageChange = (page: number) => {
        searchParams.set("unmatch_page", String(page));
        setSearchParams(searchParams);
    };

    // onChange handler for invoice list page filter
    const handleInvoicePageChange = (page: number) => {
        searchParams.set("invoice_page", String(page));
        setSearchParams(searchParams);
    };

    const handleSplitPayment = () => {
        searchParams.set("split_payment", "true");
        setSearchParams(searchParams);
    };

    const handleCancelSplitPayment = () => {
        searchParams.delete("split_payment");
        setSearchParams(searchParams);
    };

    const handleToggleSplitPaymentModal = () => {
        setShowSplitPaymentModal((state) => !state);
    };

    const handleBreakdownEnableRowSelection = (row: Row<IBillingResponse>) => {
        return (
            row.original.coinsurance_amount_collected <
            row.original.coinsurance_amount
        );
    };

    // match payment handler
    const handleMatchPayment = (isPr100?: boolean) => {
        if (getSelectedBillPayments) {
            const selectedIds = getSelectedBillPayments.map(
                (item: Row<IUnmatchedPaymentResponse>) =>
                    item.original.unmatched_payment_id
            );
            const matchPaymentPayload = {
                unmatched_payment_matches: {
                    [invoiceId]: selectedIds,
                },
                is_pr_100: isPr100,
            };
            matchPayments.mutate(matchPaymentPayload, {
                onSuccess: (response) => {
                    queryClient.setQueryData<
                        IPaginatedApiResponse<IMatchedPaymentResponse>
                    >(["get-payment"], (prev) => {
                        const prevRequired =
                            prev as IPaginatedApiResponse<IMatchedPaymentResponse>;
                        return {
                            ...prevRequired,
                            invoice_status: response.status,
                        };
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["get-payment"],
                    });
                    queryClient.setQueryData<
                        IPaginatedApiResponse<IMatchedPaymentResponse>
                    >(["client-unmatched"], (prev) => {
                        const prevRequired =
                            prev as IPaginatedApiResponse<IMatchedPaymentResponse>;
                        return {
                            ...prevRequired,
                            invoice_status: response.status,
                        };
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["client-unmatched"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: [
                            "invoices",
                            handleRemoveEmptyParamFromQueryParams({
                                invoice_id: invoiceId,
                            }),
                        ],
                    });

                    toast({
                        mode: "success",
                        message: `${buildSuccessMessageForMatchedPayments(
                            response.data.added_payments,
                            response.data.unadded_payments
                        )}`,
                    });
                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ??
                            "Could not match payment at this time, please try again later",
                    });
                },
            });
        }
    };

    // Onsubmit function for split payment form
    const onSplitPaymentFormSubmit = (payload: ISplitPaymentPayload) => {
        if (getSelectedBillsData) {
            const dataToSend = {
                payment_id: paymentInView?.unmatched_payment_id || "",
                invoices: payload.invoices.map((invoice, i) => ({
                    amount: Number(invoice.amount),
                    invoice_id:
                        getSelectedBillsData &&
                        (getSelectedBillsData[i].original.invoice_id as string),
                })),
            };

            dataToSend.invoices = [
                ...dataToSend.invoices,
                {
                    amount: Number(bill.amount),
                    invoice_id: invoiceId,
                },
            ];

            splitPayment.mutate(dataToSend, {
                onSuccess: async (res) => {
                    toast({
                        mode: "success",
                        message: res.message || "Payment spliting successful",
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["unmatched"],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["get-payment"],
                    });
                    handleToggleSplitPaymentModal();
                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not split payment at this time, please try again later",
                    });
                },
            });
        }
    };

    const columns: ColumnDef<IUnmatchedPaymentResponse>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <div>
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        id={row.original.unmatched_payment_id}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                </div>
            ),
        },

        {
            accessorKey: "payment_date",
            header: "PAYMENT DATE",
            cell: ({ row }) => (
                <span>{formatDate(row.original.payment_date) || "--"}</span>
            ),
        },
        {
            accessorKey: "amount",
            header: "AMOUNT",
            cell: ({ row }) => (
                <span>{showMoneyInAppFormat(row.original.amount) || "--"}</span>
            ),
        },
        {
            accessorKey: "payment_method",
            header: "PAYMENT METHOD",
            cell: ({ row }) => {
                const cardNumber = row.original.payment_info?.card_number;
                return (
                    <span>
                        {makeStringFirstLetterCapital(
                            row.original.payment_method
                        ) || "--"}
                        {row.original.payment_method === "card" &&
                        cardNumber ? (
                            <span> (**** {cardNumber})</span>
                        ) : null}
                    </span>
                );
            },
        },
        {
            accessorKey: "auth_code",
            header: "AUTH CODE",
            cell: ({ row }) => <span>{row.original.auth_code || "--"}</span>,
        },
        {
            accessorKey: "processor_payment_status",
            header: "PROCESSOR PAYMENT STATUS",
            cell: ({ row }) => (
                <span>
                    {makeStringFirstLetterCapital(
                        row?.original?.processor_payment_status || "--"
                    )}
                </span>
            ),
        },
        {
            accessorKey: "payment_type",
            header: "PAYMENT TYPE",
            cell: ({ row }) =>
                row.original.payment_type
                    ? getPaymentTypeTag(row.original.payment_type)
                    : "--",
        },
        {
            accessorKey: "payment_status",
            header: "BILL STATUS",
            cell: ({ row }) => {
                let bgColor: string;
                let textColor: string;

                switch (row.original.payment_status) {
                    case PaymentStatus.COMPLETED:
                        textColor = "#00563E";
                        bgColor = "rgba(204, 250, 233, 0.50)";
                        break;
                    case PaymentStatus.PENDING:
                        textColor = "#634D17";
                        bgColor = "rgba(247, 229, 164, 0.50)";
                        break;
                    case PaymentStatus.FAILED:
                        textColor = "#981F41";
                        bgColor = "rgba(251, 199, 198, 0.50)";
                        break;
                    case PaymentStatus.REFUNDED:
                        textColor = "#165574";
                        bgColor = "rgba(172, 222, 250, 0.50)";
                        break;
                    case PaymentStatus.VOIDED:
                        textColor = "#981F41";
                        bgColor = "rgba(251, 199, 198, 0.50)";
                        break;
                    default:
                        textColor = "#165574";
                        bgColor = "rgba(172, 222, 250, 0.50)";
                }

                return (
                    <Badge
                        bgColor={bgColor}
                        textColor={textColor}
                        type="custom"
                        content={row.original.payment_status}
                    />
                );
            },
        },
        {
            accessorKey: "start_date_time",
            header: "DESCRIPTION",
            cell: ({ row }) =>
                row.original?.start_date_time
                    ? `Payment for ${formatDate(row.original.start_date_time)}`
                    : "--",
        },
    ];

    const invoicesColumns: ColumnDef<IBillingResponse>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <div
                    hidden={
                        row.original.coinsurance_amount_collected >=
                        row.original.coinsurance_amount
                    }
                >
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        id={row.original.invoice_id}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                </div>
            ),
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "client_name",
                    header: "CLIENT",
                    cell: ({ row }) =>
                        displayNameInRightFormat({
                            firstName: row.original.client.first_name,
                            lastName: row.original.client.last_name,
                        }),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "date_of_service",
                    header: "DATE OF SERVICE",
                    cell: ({ row }) => (
                        <span>
                            {formatDate(row.original.date_of_service) || "--"}
                        </span>
                    ),
                },
            ],
        },
        {
            accessorKey: "cpt_code",
            header: "CPT CODE",
            cell: ({ row }) => {
                if (row.original.note_type === NoteTypes.CANCELLATION_NOTE) {
                    return "NO SHOW";
                }

                if (row.original.cpt_code) {
                    return (
                        <Tooltip
                            content={
                                CPT_CODES_DESCRIPTION[
                                    row.original.cpt_code as string
                                ] || ""
                            }
                        >
                            <span
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    columnGap: "5px",
                                }}
                            >
                                {removeEnumUnderscore(row.original.cpt_code)}
                                <InfoIcon />
                            </span>
                        </Tooltip>
                    );
                }

                return "--";
            },
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "note_type",
                    header: "NOTE",
                    cell: ({ row }) =>
                        row.original.note_type
                            ? NoteLabels[row.original.note_type as string]
                            : "--",
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "provider_name",
                    header: "CLINICIAN",
                    cell: ({ row }) => (
                        <span>
                            {`${row.original.provider.first_name} ${row.original.provider.last_name}` ||
                                "--"}
                        </span>
                    ),
                },
            ],
        },
        {
            header: " ",
            columns: [
                {
                    accessorKey: "insurance_provider",
                    header: "INS PROVIDER",
                    cell: ({ row }) => (
                        <span>{row.original.insurance_provider || "--"}</span>
                    ),
                },
            ],
        },
        {
            header: "INSURANCE",
            columns: [
                {
                    accessorKey: "insurance_charge",
                    header: "CHARGE",
                    cell: ({ row }) =>
                        showMoneyInAppFormat(row.original.insurance_amount),
                },
                {
                    accessorKey: "insurance_amount_collected",
                    header: "COLLECTED",
                    cell: ({ row }) =>
                        showMoneyInAppFormat(
                            row.original.insurance_amount_collected
                        ),
                },
            ],
        },
        {
            header: "CO-INSURANCE",
            columns: [
                {
                    accessorKey: "coinsurance_charge",
                    header: "CHARGE",
                    cell: ({ row }) =>
                        showMoneyInAppFormat(row.original.coinsurance_amount),
                },
                {
                    accessorKey: "coinsurance_amount_collected",
                    header: "COLLECTED",
                    cell: ({ row }) =>
                        showMoneyInAppFormat(
                            row.original.coinsurance_amount_collected
                        ),
                },
            ],
        },

        {
            accessorKey: "invoice_status",
            header: "BILL STATUS",
            cell: ({ row }) => (
                <span>
                    {getBillStatusTag(
                        row.original.invoice_status as BillStatus
                    )}
                </span>
            ),
        },
    ];

    return (
        <>
            <Dialog open={showSplitPaymentModal}>
                <DialogContent
                    title="Split Payment"
                    variant="center"
                    handleCloseDialog={() => {
                        handleToggleSplitPaymentModal();
                        reset();
                    }}
                    saveText="Split payment"
                    submitBtnFormValue="split-payment"
                    isSubmitBtnDisabled={
                        splitPayment.isLoading ||
                        (formInvoices &&
                            bill &&
                            [...formInvoices, bill].reduce(
                                (accumulator, currentValue) =>
                                    accumulator + Number(currentValue.amount),
                                0
                            ) !== paymentInView?.amount)
                    }
                    isCancelBtnDisabled={
                        splitPayment.isLoading ||
                        (formInvoices &&
                            bill &&
                            [...formInvoices, bill].reduce(
                                (accumulator, currentValue) =>
                                    accumulator + currentValue.amount,
                                0
                            ) !== paymentInView?.amount)
                    }
                >
                    <>
                        <div>
                            <Alert
                                type="info"
                                title="Split payment on selected bills"
                                description={`Total amount of bills selected to split payment across must equal ${
                                    paymentInView
                                        ? showMoneyInAppFormat(
                                              paymentInView.amount
                                          )
                                        : "Payment amount"
                                }`}
                            />
                        </div>
                        <div className="mt-[--spacing-header]">
                            <p className="text-caption font-medium mb-2">
                                Input amount to split across bills
                            </p>
                            <form
                                onSubmit={handleSubmit(
                                    onSplitPaymentFormSubmit
                                )}
                                id="split-payment"
                            >
                                {getSelectedBillPayments && (
                                    <div className="[border-bottom:1px_solid_var(--color-stroke)] [border-top:1px_solid_var(--color-stroke)] py-[--spacing-items]">
                                        <div className="flex justify-between text-caption font-semibold items-center mb-1">
                                            <p className="text-text">
                                                {`Open Bill: ${formatDate(
                                                    dateOfService
                                                )}`}
                                            </p>
                                            <p className="text-danger">
                                                {`Balance:
                                                    ${showMoneyInAppFormat(
                                                        Math.abs(invoiceBalance)
                                                    )}`}
                                            </p>
                                        </div>
                                        <Input
                                            {...register(`bill.amount`)}
                                            label="Split amount"
                                            hasError={!!errors?.bill?.amount}
                                            errorText={
                                                errors?.bill?.amount?.message
                                            }
                                            showCurrency
                                        />
                                    </div>
                                )}
                                {getSelectedBillsData?.map(
                                    (
                                        invoice: Row<IBillingResponse>,
                                        i: number
                                    ) => {
                                        const balance =
                                            invoice.original
                                                .coinsurance_amount -
                                                invoice.original
                                                    .coinsurance_amount_collected <=
                                            0
                                                ? 0
                                                : invoice.original
                                                      .coinsurance_amount -
                                                  invoice.original
                                                      .coinsurance_amount_collected;
                                        return (
                                            <div
                                                className="py-[--spacing-items] [border-bottom:1px_solid_var(--color-stroke)] last:[border-bottom:none]"
                                                key={
                                                    invoice.original.invoice_id
                                                }
                                            >
                                                <div className="flex justify-between text-caption font-semibold items-center mb-1">
                                                    <p className="text-text">
                                                        {`Open Bill: ${formatDate(
                                                            invoice.original
                                                                .date_of_service
                                                        )}`}
                                                    </p>
                                                    <p className="text-danger">
                                                        {`Balance:
                                                    ${showMoneyInAppFormat(
                                                        balance
                                                    )}`}
                                                    </p>
                                                </div>
                                                <Input
                                                    {...register(
                                                        `invoices.${i}.amount`
                                                    )}
                                                    label="Split amount"
                                                    hasError={
                                                        !!errors?.invoices?.[i]
                                                            ?.amount
                                                    }
                                                    errorText={
                                                        errors?.invoices?.[i]
                                                            ?.amount?.message
                                                    }
                                                    showCurrency
                                                />
                                            </div>
                                        );
                                    }
                                )}
                            </form>
                        </div>
                    </>
                </DialogContent>
            </Dialog>
            <div
                className={cn({
                    "flex justify-between": canSplitPayment,
                })}
            >
                <div
                    className={cn("max-h-[90dvh] overflow-y-scroll", {
                        "w-[49%]": canSplitPayment,
                    })}
                >
                    <Card classNames="flex gap-x-4 mb-[--spacing-header]">
                        <div className="flex flex-col items-start gap-y-1">
                            <span className="uppercase text-textSec font-medium text-caption">
                                Total NO OF UNMATCHED
                            </span>
                            <span className="capitalize font-regular text-base">
                                {isLoading && <Loader loaderType="inline" />}
                                {error && "0"}
                                {data?.total_count ||
                                    (!isLoading && !error && "0")}
                            </span>
                        </div>
                        <div className="flex flex-col items-start gap-y-1">
                            <span className="uppercase text-textSec font-medium text-caption">
                                Total Amount of unmatched
                            </span>
                            <span className="capitalize font-regular text-base">
                                {isLoading && <Loader loaderType="inline" />}
                                {error ? showMoneyInAppFormat(0) : null}
                                {data?.total_amount
                                    ? showMoneyInAppFormat(data?.total_amount)
                                    : !isLoading &&
                                      !error &&
                                      showMoneyInAppFormat(0)}
                            </span>
                        </div>
                    </Card>
                    <div className="bg-[--card-bg-gray] px-[--padding-card-x-lg] py-[--padding-card-y-lg] mb-[--spacing-header]">
                        <h3 className="font-semibold text-base capitalize mb-[--spacing-header]">
                            Payment Details
                        </h3>
                        <Card classNames="flex gap-x-4">
                            <div className="flex flex-col items-start gap-y-1">
                                <span className="uppercase text-textSec font-medium text-caption">
                                    Balance
                                </span>
                                <span
                                    className={cn(
                                        "capitalize font-regular text-base",
                                        {
                                            "text-danger": invoiceBalance > 0,
                                        }
                                    )}
                                >
                                    {invoiceBalance <= 0 ? (
                                        showMoneyInAppFormat(
                                            Math.abs(invoiceBalance)
                                        )
                                    ) : (
                                        <>
                                            (
                                            {showMoneyInAppFormat(
                                                invoiceBalance
                                            )}
                                            )
                                        </>
                                    )}
                                </span>
                            </div>
                            <div className="flex flex-col items-start gap-y-1">
                                <span className="uppercase text-textSec font-medium text-caption">
                                    SELECTED AMOUNT
                                </span>
                                <span className="capitalize font-regular text-base">
                                    {showMoneyInAppFormat(total || 0)}
                                </span>
                            </div>
                            <div className="flex flex-col items-start gap-y-1">
                                <span className="uppercase text-textSec font-medium text-caption">
                                    Filter By Payment Status
                                </span>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {unmatchPaymentStatusFilter ||
                                                "All Payments"}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent
                                        width={150}
                                        className="overflow-y-auto capitalize"
                                    >
                                        <DropdownItem
                                            onClick={() =>
                                                handlePaymentStatusFilter("")
                                            }
                                            isSelected={
                                                !unmatchPaymentStatusFilter
                                            }
                                        >
                                            All Payments
                                        </DropdownItem>
                                        {Object.values(PaymentStatus).map(
                                            (item) => (
                                                <DropdownItem
                                                    key={item}
                                                    onClick={() =>
                                                        handlePaymentStatusFilter(
                                                            item
                                                        )
                                                    }
                                                    isSelected={
                                                        unmatchPaymentStatusFilter ===
                                                        item
                                                    }
                                                >
                                                    {item}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                            <div className="flex flex-col items-start gap-y-1">
                                <span className="uppercase text-textSec font-medium text-caption">
                                    Filter By Payment Type
                                </span>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>
                                            {getPaymentTypeFilterText()}
                                        </FilterButton>
                                    </DropdownTrigger>

                                    <DropdownContent width={250}>
                                        <DropdownItem
                                            onClick={() =>
                                                handlePaymentTypeFilterChange(
                                                    "All"
                                                )
                                            }
                                            isSelected={
                                                paymentTypeFilter === "All"
                                            }
                                        >
                                            All
                                        </DropdownItem>
                                        <DropdownItem
                                            onClick={() =>
                                                handlePaymentTypeFilterChange(
                                                    "Credit"
                                                )
                                            }
                                            isSelected={
                                                paymentTypeFilter === "Credit"
                                            }
                                        >
                                            Credit
                                        </DropdownItem>
                                        <DropdownItem
                                            onClick={() =>
                                                handlePaymentTypeFilterChange(
                                                    "Write-off"
                                                )
                                            }
                                            isSelected={
                                                paymentTypeFilter ===
                                                "Write-off"
                                            }
                                        >
                                            Write-off
                                        </DropdownItem>
                                        <DropdownItem
                                            onClick={() =>
                                                handlePaymentTypeFilterChange(
                                                    "Refund"
                                                )
                                            }
                                            isSelected={
                                                paymentTypeFilter === "Refund"
                                            }
                                        >
                                            Refund
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        </Card>
                        {total && total < Math.abs(invoiceBalance) ? (
                            <p className="my-1.5 text-caption text-danger">
                                The selected fee is lower than the client&apos;s
                                balance
                            </p>
                        ) : null}
                    </div>
                    {isLoading && <Loader />}
                    {error && error?.response?.status !== 404 && (
                        <ListState
                            isError
                            stateHelperText="Try reloading this page to solve this issue"
                            errorMsg={
                                error?.response?.data.message ||
                                `Cannot display unmatched payments at this time please try again later`
                            }
                        />
                    )}
                    {data && isSuccess && data.data.length === 0 && (
                        <ListState
                            stateHelperText="Unmatched payments will appear here"
                            isError={false}
                            emptyMessage="There are no unmatched payments yet"
                        />
                    )}
                    {isSuccess && data && data.data.length > 0 && (
                        <>
                            {getTotalAmountOfTableRowsSelected(rowSelection)
                                .amount > 0 &&
                                !canSplitPayment && (
                                    <div className="flex items-center mb-[--spacing-items] gap-x-[--spacing-items]">
                                        <div className="font-medium text-base">
                                            {
                                                getTotalAmountOfTableRowsSelected(
                                                    rowSelection
                                                ).amountString
                                            }
                                            :
                                        </div>
                                        <Button
                                            variant="primary"
                                            onClick={() => handleMatchPayment()}
                                            disabled={matchPayments.isLoading}
                                        >
                                            Match as coinsurance
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() =>
                                                handleMatchPayment(true)
                                            }
                                            disabled={matchPayments.isLoading}
                                        >
                                            Match as PR100
                                        </Button>

                                        {getSelectedBillPayments &&
                                            getSelectedBillPayments.length ===
                                                1 &&
                                            !canSplitPayment && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={handleSplitPayment}
                                                >
                                                    Split payment
                                                </Button>
                                            )}
                                    </div>
                                )}
                            <Table
                                columns={columns}
                                data={data?.data}
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                setGetSelectedData={setGetSelectedBillPayments}
                                hasPagination={data.total_count > 20}
                                enableRowSelection={!canSplitPayment}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={unmatchPageFilter}
                                        onPageChange={
                                            handleUnmatchedPaymentPageChange
                                        }
                                        limit={unmatchLimitFilter}
                                    />
                                }
                            />
                        </>
                    )}
                </div>
                {canSplitPayment && (
                    <div
                        className={cn("max-h-[90dvh] overflow-y-scroll", {
                            "w-[49%]": canSplitPayment,
                        })}
                    >
                        {invoices.error &&
                            invoices.error?.response?.status !== 404 && (
                                <ListState
                                    isError
                                    stateHelperText="Try reloading this page to solve this issue"
                                    errorMsg={
                                        invoices.error?.response?.data
                                            .message ||
                                        `Cannot display invoices at this time please try again later`
                                    }
                                />
                            )}
                        {invoices.isLoading && <Loader />}

                        {invoices.isSuccess &&
                            invoices.data &&
                            invoices.data.data.length > 0 && (
                                <>
                                    <div className="flex items-center mb-[--spacing-items] gap-x-[--spacing-items]">
                                        {getSelectedBillsData &&
                                            getSelectedBillsData.length > 0 && (
                                                <Button
                                                    onClick={
                                                        handleToggleSplitPaymentModal
                                                    }
                                                >
                                                    Complete split payment
                                                </Button>
                                            )}
                                        <Button
                                            variant="secondary"
                                            onClick={handleCancelSplitPayment}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                    <Table
                                        columns={invoicesColumns}
                                        data={invoices.data?.data.filter(
                                            (item) =>
                                                item.invoice_id !== invoiceId
                                        )}
                                        rowSelection={rowSelectionInvoices}
                                        setRowSelection={
                                            setRowSelectionInvoices
                                        }
                                        setGetSelectedData={
                                            setGetSelectedBillsData
                                        }
                                        enableRowSelection={
                                            handleBreakdownEnableRowSelection
                                        }
                                        hasPagination={
                                            invoices.data.total_count > 20
                                        }
                                        pagination={
                                            <Pagination
                                                totalCount={
                                                    invoices.data.total_count
                                                }
                                                totalPages={
                                                    invoices.data.total_pages
                                                }
                                                currentPage={invoicesPageFilter}
                                                onPageChange={
                                                    handleInvoicePageChange
                                                }
                                                limit={invoicesLimitFilter}
                                            />
                                        }
                                    />
                                </>
                            )}
                    </div>
                )}
            </div>
        </>
    );
}
